/** Native transport contract: server-issued session-bound intent -> system
 * browser -> opaque completion code -> CSRF POST in this original WebView.
 * Caller refreshes SessionProvider after success; no navigation or storage,
 * preserving renderer/recovery state. Older shells return available=false;
 * callers must offer the hosted/browser path, never embedded-provider OAuth.
 * The provider-independent server staging contract lives in native_auth.py.
 */
import { apiPost } from "@/api/client";

type Invoke = (
  command: string,
  args?: Record<string, unknown>,
) => Promise<unknown>;
type NativeWindow = Window & {
  __TAURI_INTERNALS__?: { invoke?: Invoke };
};
export type NativeAuthPurpose = "login" | "connect";
export type NativeAuthCapabilities = {
  version: number;
  oauth: boolean;
  apple: boolean;
};

const CALLBACK = "tiltracing://oauth-callback/complete";
const OPAQUE = /^[A-Za-z0-9_-]{43}$/;
let inFlight = false;
let generation = 0;

function invoke(): Invoke | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as NativeWindow).__TAURI_INTERNALS__?.invoke;
}

export async function nativeAuthCapabilities(): Promise<NativeAuthCapabilities> {
  const unavailable = { version: 0, oauth: false, apple: false };
  const ipc = invoke();
  if (!ipc) return unavailable;
  try {
    const result = (await ipc(
      "native_auth_capabilities",
    )) as Partial<NativeAuthCapabilities> | null;
    return result?.version === 1 && result.oauth === true
      ? { version: 1, oauth: true, apple: false }
      : unavailable;
  } catch {
    return unavailable;
  }
}

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/, "");
}

export async function createNativeProof(): Promise<{
  state: string;
  verifier: string;
  challenge: string;
}> {
  const state = base64Url(crypto.getRandomValues(new Uint8Array(32)));
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
  const hash = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return { state, verifier, challenge: base64Url(new Uint8Array(hash)) };
}

export function nativeCompletionCode(callback: string, state: string): string {
  const url = new URL(callback);
  const keys = [...url.searchParams.keys()];
  if (
    `${url.protocol}//${url.host}${url.pathname}` !== CALLBACK ||
    url.username ||
    url.password ||
    url.hash ||
    keys.length !== 2 ||
    !keys.includes("state") ||
    !keys.includes("code") ||
    url.searchParams.get("state") !== state ||
    !OPAQUE.test(url.searchParams.get("code") ?? "")
  )
    throw new Error("native_callback_invalid");
  return url.searchParams.get("code")!;
}

export async function cancelNativeAuth(): Promise<void> {
  generation += 1;
  try {
    await invoke()?.("cancel_native_oauth");
  } catch {
    /* old shells have no command */
  }
}

export async function startNativeAuth(
  provider: string,
  purpose: NativeAuthPurpose = "login",
): Promise<void> {
  if (inFlight) throw new Error("native_auth_in_progress");
  inFlight = true;
  const attempt = ++generation;
  const checkCancelled = () => {
    if (attempt !== generation) throw new Error("user_cancelled");
  };
  try {
    if (!(await nativeAuthCapabilities()).oauth)
      throw new Error("native_auth_unavailable");
    checkCancelled();
    const proof = await createNativeProof();
    checkCancelled();
    const intent = await apiPost<{
      intent: string;
      state: string;
      startUrl: string;
    }>("/session/native/intent", {
      provider,
      purpose,
      state: proof.state,
      challenge: proof.challenge,
      challengeMethod: "S256",
    });
    checkCancelled();
    const start = new URL(intent.startUrl, window.location.origin);
    if (
      start.protocol !== "https:" ||
      start.origin !== window.location.origin ||
      start.pathname !== "/api/v1/session/native/start" ||
      start.hash ||
      start.username ||
      start.password ||
      intent.state !== proof.state ||
      !OPAQUE.test(intent.intent) ||
      [...start.searchParams.keys()].length !== 1 ||
      start.searchParams.get("intent") !== intent.intent
    )
      throw new Error("native_intent_invalid");
    const callback = await invoke()!("start_native_oauth", {
      url: start.href,
      state: proof.state,
    });
    checkCancelled();
    if (typeof callback !== "string")
      throw new Error("native_callback_invalid");
    const code = nativeCompletionCode(callback, proof.state);
    await apiPost("/session/native/redeem", { code, verifier: proof.verifier });
  } finally {
    inFlight = false;
  }
}
